import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Badge,
  Input,
  Modal,
  Select,
  Button,
  Tooltip,
  LinkButton,
  DatePicker,
  ActionsWrap,
  SearchableTable,
  SearchableColumn,
} from 'design-system';

import { paths } from 'src/routes/paths';

import { useDepartmentsByPrivilege } from 'src/hooks/use-departments-by-privilege';

import { sortByCreatedAt } from 'src/utils/sort';

import { PrivilegeType } from 'src/services/role';
import { queryKeys } from 'src/constants/queryKeys';
import { User, getAllUsers } from 'src/services/user';
import { getScrollConfig, getUserFullName, toLocalISOString } from 'src/utils';
import { ActionPlan, FilterActionPlan, ActionPlanStatus } from 'src/services/actionPlan';

// ProgressBar组件
interface ProgressBarProps {
  percent: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ percent }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ marginRight: 8, minWidth: '50px' }}>{percent}%</span>
    <div style={{ width: '100px', position: 'relative' }}>
      <div
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#ebeff7',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percent > 100 ? 100 : percent}%`,
            height: '100%',
            backgroundColor: percent >= 100 ? '#52c41a' : '#105FC8',
            borderRadius: '4px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  </div>
);

type StatusType = ActionPlanStatus;

// 状态映射
const statusMap: Record<StatusType, { text: string; color: string }> = {
  [ActionPlanStatus.NotStarted]: { text: '--', color: '#FFB133' },

  [ActionPlanStatus.Completed]: { text: '已完成', color: '#3CC47C' },
};

// 来源映射
const sourceMap: Record<string, string> = {
  businessplan: '目标',
  manualcreated: '手动创建',
  project: '项目策略',
  varianceanalysis: '差异分析',
  plan: '目标拆解',
};

// 新增定义搜索表单参数的接口
interface SearchFormValues {
  code?: string;
  source?: string;
  description?: string;
  departmentId?: string | string[]; // 支持单选和多选
  coordinatorIds?: number[];
  actualEndDateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  actualStartDateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  planDateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  [key: string]: unknown; // 添加索引签名
}

type Props = {
  data: ActionPlan[];
  loading: boolean;
  enabled: boolean;
  total: number;
  currentPage: number;
  pageSize: number;
  onEdit?: (record: ActionPlan) => void;
  onView?: (record: ActionPlan) => void;
  onDelete?: (record: ActionPlan) => void;
  onViewCalendar?: () => void;
  onPageChange?: (page: number) => void;
  onShowSizeChange?: (current: number, size: number) => void;
  onFilter?: (filter: FilterActionPlan | null) => void;
  onResetFilter?: () => void;
  slot?: React.ReactElement;
  initialFilter?: FilterActionPlan | null; // 用于回填表单的初始过滤参数
};

export default function ActionPlanListTable({
  data,
  loading,
  total,
  currentPage,
  pageSize,
  onEdit,
  onView,
  onDelete,
  onViewCalendar,
  onPageChange,
  onShowSizeChange,
  onFilter,
  onResetFilter,
  enabled,
  slot,
  initialFilter,
}: Props) {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // 按 ACTION_PLAN 权限获取可选部门
  const { departmentOptions, allDepartments: departmentsData } = useDepartmentsByPrivilege(
    PrivilegeType.ACTION_PLAN,
    { staleTime: 0 }
  );

  // 获取所有用户
  const { data: allUsers } = useQuery({
    queryKey: [queryKeys.getAllUsers],
    queryFn: getAllUsers,
    staleTime: 1000 * 60 * 10, // 10分钟
  });

  // 处理删除确认
  const handleDelete = (record: ActionPlan) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除行动计划"${record.description || record.code || '未命名'}"吗？此操作不可撤销。`,
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        onDelete?.(record);
      },
    });
  };

  // 处理初始过滤参数回填
  useEffect(() => {
    if (initialFilter) {
      const formValues: Partial<SearchFormValues> = {};

      // 回填部门ID，优先使用departmentIds（多选），其次使用departmentId（单选）
      if (initialFilter.departmentIds && initialFilter.departmentIds.length > 0) {
        formValues.departmentId = initialFilter.departmentIds;
      } else if (initialFilter.departmentId) {
        formValues.departmentId = initialFilter.departmentId;
      }

      // 回填其他字段
      if (initialFilter.code) formValues.code = initialFilter.code;
      if (initialFilter.description) formValues.description = initialFilter.description;
      if (initialFilter.source) formValues.source = initialFilter.source;
      if (initialFilter.coordinatorIds) formValues.coordinatorIds = initialFilter.coordinatorIds;

      // 设置表单值
      form.setFieldsValue(formValues);
    }
  }, [initialFilter, form]);

  // 只在部门数据加载完成且有筛选条件时输出关键信息
  useEffect(() => {
    if (initialFilter?.departmentIds?.length && departmentsData?.length) {
      const matchedCount = departmentsData.filter((dept) =>
        initialFilter.departmentIds?.includes(dept.id)
      ).length;
    }
  }, [initialFilter?.departmentIds, departmentsData]);

  // 处理搜索，将any类型替换为具体的SearchFormValues接口
  const handleSearch = (values: SearchFormValues) => {
    if (onFilter) {
      // 判断是否有过滤条件
      if (
        Object.keys(values).some((key) => {
          const value = values[key];
          return value !== undefined && value !== '' && value !== null;
        })
      ) {
        // 创建一个空的过滤参数对象
        const filterParams: FilterActionPlan = {};

        // 处理来源
        if (values.source) {
          filterParams.source = values.source;
        }

        // 处理编码
        if (values.code) {
          filterParams.code = values.code;
        }

        // 处理行动计划描述
        if (values.description) {
          filterParams.description = values.description;
        }

        // 处理考核部门
        if (values.departmentId) {
          // 统一使用departmentIds数组格式
          if (Array.isArray(values.departmentId) && values.departmentId.length > 0) {
            filterParams.departmentIds = values.departmentId;
          } else if (typeof values.departmentId === 'string') {
            filterParams.departmentIds = [values.departmentId];
          }
        }

        // 处理责任人
        if (values.coordinatorIds && values.coordinatorIds.length > 0) {
          filterParams.coordinatorIds = values.coordinatorIds;
        }

        // 处理实际完成时间范围
        if (
          values.actualEndDateRange?.length === 2 &&
          values.actualEndDateRange[0] &&
          values.actualEndDateRange[1]
        ) {
          filterParams.actualEndDate = {
            min: toLocalISOString(values.actualEndDateRange[0].startOf('day').toDate()),
            max: toLocalISOString(values.actualEndDateRange[1].endOf('day').toDate()),
          };
        }

        // 处理实际开始时间范围
        if (
          values.actualStartDateRange?.length === 2 &&
          values.actualStartDateRange[0] &&
          values.actualStartDateRange[1]
        ) {
          filterParams.actualStartDate = {
            min: toLocalISOString(values.actualStartDateRange[0].startOf('day').toDate()),
            max: toLocalISOString(values.actualStartDateRange[1].endOf('day').toDate()),
          };
        }

        // 处理计划周期范围
        if (
          values.planDateRange?.length === 2 &&
          values.planDateRange[0] &&
          values.planDateRange[1]
        ) {
          filterParams.planStartDate = {
            min: toLocalISOString(values.planDateRange[0].startOf('day').toDate()),
            max: toLocalISOString(values.planDateRange[1].endOf('day').toDate()),
          };
        }

        onFilter(filterParams);
      } else {
        onFilter(null);
        onResetFilter?.();
      }
    }
  };

  const columns: SearchableColumn<ActionPlan>[] = [
    // {
    //   title: '编号',
    //   dataIndex: 'id',
    //   width: 80,
    //   render: (_, __, index) => index + 1,
    // },
    {
      title: '编号',
      dataIndex: 'code',
      width: 100,
      search: <Input placeholder="请输入编码" allowClear />,
      render: (code) => code || '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 120,
      search: (
        <Select
          placeholder="请选择来源"
          allowClear
          showSearch
          options={[
            { label: '手动创建', value: 'ManualCreated' },
            // { label: '目标拆解', value: 'plan' },
            { label: '差异分析', value: 'VarianceAnalysis' },
            { label: '项目策略', value: 'Project' },
          ]}
          filterOption={(input, option) => {
            const label = option?.label?.toString().toLowerCase() || '';
            return label.includes(input.toLowerCase());
          }}
        />
      ),
      render: (source, record) => {
        // 获取source的标准化版本（转为大写以便比较）
        const normalizedSource = typeof source === 'string' ? source.toUpperCase() : '';

        // 查找sourceMap中匹配的键（不区分大小写）
        const sourceKey = Object.keys(sourceMap).find(
          (key) => key.toUpperCase() === normalizedSource
        );

        // 获取显示文本
        const sourceText = sourceKey ? sourceMap[sourceKey] : source || '-';

        // 如果是手动创建，不需要任何操作
        if (!source || normalizedSource === 'MANUALCREATED') {
          return sourceText;
        }

        // 如果是项目策略，点击跳转到项目详情页
        if (normalizedSource === 'PROJECT' && record.project?.id) {
          return (
            <LinkButton
              onClick={() => {
                navigate(
                  `${paths.dashboard.objectivesManagement.projectManagement.detail}?id=${record.project?.id}`
                );
              }}
            >
              {sourceText}
            </LinkButton>
          );
        }

        // 如果是差异分析，点击跳转到差异分析详情页
        if (normalizedSource === 'VARIANCEANALYSIS' && record.varianceAnalysis?.id) {
          return (
            <LinkButton
              onClick={() => {
                navigate(
                  `${paths.dashboard.analysis.targetDifference.detail}?id=${record.varianceAnalysis?.id}`
                );
              }}
            >
              {sourceText}
            </LinkButton>
          );
        }

        return sourceText;
      },
    },
    {
      title: '考核部门',
      dataIndex: 'department',
      width: 120,
      searchName: 'departmentId',
      search: (
        <Select
          mode="multiple"
          placeholder="请选择考核部门"
          allowClear
          showSearch
          value={initialFilter?.departmentIds}
          options={departmentOptions}
          onChange={() => {
            // 部门选择变化处理
          }}
          onDropdownVisibleChange={() => {
            // 日志已在useEffect中处理，避免重复输出
          }}
          filterOption={(input, option) => {
            const label = option?.label?.toString().toLowerCase() || '';
            return label.includes(input.toLowerCase());
          }}
        />
      ),
      render: (department) => department?.name || '-',
    },
    {
      title: '责任人',
      dataIndex: 'coordinators',
      width: 120,
      searchName: 'coordinatorIds',
      search: (
        <Select
          mode="multiple"
          placeholder="请选择责任人"
          allowClear
          showSearch
          options={
            allUsers?.map((user: User) => ({
              label: getUserFullName(user),
              value: user.id,
            })) || []
          }
          filterOption={(input, option) => {
            const label = option?.label?.toString().toLowerCase() || '';
            return label.includes(input.toLowerCase());
          }}
        />
      ),
      render: (coordinators) =>
        coordinators
          ?.map((c: { lastName: string; firstName: string }) => `${c.lastName}${c.firstName}`)
          .join(', ') || '-',
    },
    {
      title: '行动计划描述',
      dataIndex: 'description',
      width: 200,
      search: <Input placeholder="请输入行动计划描述" allowClear />,
      render: (description) => (
        <Tooltip title={description || '-'}>
          <div
            style={{
              maxWidth: '100%',
              maxHeight: '40px',
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description || '-'}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '任务周期',
      dataIndex: 'dateRange',
      width: 220,
      searchName: 'planDateRange',
      search: <DatePicker.RangePicker style={{ width: '100%' }} allowClear />,
      render: (_, record) => {
        const startDate = record.planStartDate
          ? dayjs(record.planStartDate).format('YYYY-MM-DD')
          : '-';
        const endDate = record.planEndDate ? dayjs(record.planEndDate).format('YYYY-MM-DD') : '-';
        return `${startDate} 至 ${endDate}`;
      },
    },
    {
      title: '进度状态',
      dataIndex: 'status',
      width: 180,
      render: (status, record) => {
        // 状态信息类型定义
        type StatusInfo =
          | { text: string; color: string; isProgressBar?: false }
          | { component: React.ReactElement; isProgressBar: true };

        // 获取详细的进度状态信息
        const getDetailedProgressStatus = (record: ActionPlan): StatusInfo => {
          const { status, assessmentDimension, actualEndDate, planEndDate, metadata } = record;
          const currentStatus = status || ActionPlanStatus.NotStarted;

          // 如果 status 为 null，不管 assessmentDimension 是什么，都显示 "--"
          if (status === null) {
            return { text: '--', color: '#666' };
          }

          // 当 assessmentDimension != 'Quantity' 且 status == 'NotStarted' 时，显示"未完成"
          if (assessmentDimension !== 'Quantity' && currentStatus === ActionPlanStatus.NotStarted) {
            return { text: '未完成', color: '#FF4D4F' };
          }

          // 对于数量评估维度，如果有状态值，显示进度条
          if (assessmentDimension === 'Quantity' && status !== null) {
            const planQuantity = metadata?.planQuantity || 0;
            const actualQuantity = metadata?.actualQuantity || 0;

            // 计算百分比
            let percent = 0;
            if (planQuantity && planQuantity !== 0) {
              percent = (actualQuantity / planQuantity) * 100;
              percent = parseFloat(percent.toFixed(2));
            }

            return {
              component: <ProgressBar percent={percent} />,
              isProgressBar: true,
            };
          }

          // 如果状态不是已完成，返回原状态
          if (currentStatus !== ActionPlanStatus.Completed) {
            const statusInfo = statusMap[currentStatus];
            return { text: statusInfo.text, color: statusInfo.color };
          }

          // 如果是已完成状态，且assessmentDimension为Duration，则检查时间完成情况
          if (assessmentDimension === 'Duration' && actualEndDate && planEndDate) {
            const actualEnd = dayjs(actualEndDate);
            const planEnd = dayjs(planEndDate);

            if (actualEnd.isBefore(planEnd)) {
              return { text: '提前完成', color: '#52c41a' }; // 绿色
            }
            if (actualEnd.isSame(planEnd, 'day')) {
              return { text: '按时完成', color: '#1890ff' }; // 蓝色
            }
            return { text: '延后完成', color: '#faad14' }; // 橙色
          }

          // 其他情况显示已完成
          return { text: '已完成', color: '#52c41a' }; // 绿色
        };

        const statusInfo = getDetailedProgressStatus(record);

        // 如果是进度条组件，直接返回组件
        if (statusInfo.isProgressBar) {
          return statusInfo.component;
        }

        // 否则返回普通的Badge，但如果文本是"--"则只显示文本
        if (statusInfo.text === '--') {
          return <span style={{ color: '#666' }}>--</span>;
        }
        return <Badge color={statusInfo.color} text={statusInfo.text} />;
      },
    },
    // {
    //   title: '实际开始',
    //   dataIndex: 'actualStartDate',
    //   width: 160,
    //   searchName: 'actualStartDateRange',
    //   search: <DatePicker.RangePicker style={{ width: '100%' }} allowClear />,
    //   render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
    // },
    {
      title: '实际完成',
      dataIndex: 'actualEndDate',
      width: 160,
      searchName: 'actualEndDateRange',
      search: <DatePicker.RangePicker style={{ width: '100%' }} allowClear />,
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      width: 100,
      render: (_, record) => {
        const displayText = record.creator
          ? `${record.creator.lastName}${record.creator.firstName}`
          : '-';
        return (
          <Tooltip title={displayText}>
            <div
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayText}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (_, record) =>
        record.createdAt ? dayjs(record.createdAt).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '维护人',
      dataIndex: 'lastUpdater',
      width: 100,
      render: (lastUpdater) => {
        const displayText = lastUpdater ? `${lastUpdater.lastName}${lastUpdater.firstName}` : '-';
        return (
          <Tooltip title={displayText}>
            <div
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayText}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '维护时间',
      dataIndex: 'updatedAt',
      width: 160,
      render: (_, record) =>
        record.updatedAt ? dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <ActionsWrap>
          <LinkButton onClick={() => onView?.(record)}>详情</LinkButton>
          <LinkButton onClick={() => onEdit?.(record)}>编辑</LinkButton>
          <LinkButton onClick={() => handleDelete(record)}>删除</LinkButton>
        </ActionsWrap>
      ),
    },
  ];

  return (
    <div>
      <SearchableTable
        searchEnabled={enabled}
        form={form}
        cacheColumnsKey="amoeba-actionPlanList"
        bordered
        columns={columns}
        dataSource={Array.isArray(data) ? sortByCreatedAt(data) : []}
        loading={loading}
        rowKey="id"
        scroll={getScrollConfig(data)}
        onSearch={handleSearch}
        slot={slot}
        action={
          <Button
            type="text"
            style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: 14,
              fontWeight: 400,
              height: 32,
            }}
            onClick={onViewCalendar}
          >
            <img
              src="/assets/icons/common/icon-wrapper.svg"
              alt="日程表"
              style={{
                width: 16,
                height: 16,
                marginRight: -4,
                display: 'inline-block',
                verticalAlign: 'middle',
              }}
            />
            <span style={{ verticalAlign: 'middle' }}>日程表</span>
          </Button>
        }
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: onPageChange,
          onShowSizeChange,
          showQuickJumper: true,
          showTotal: (t) => `共 ${t} 条记录`,
        }}
      />
    </div>
  );
}
